import { useState } from "react";
import { AuthPageLayout } from "./AuthPageLayout";
import { LoadingButton } from "./LoadingButton";
import { PasswordField } from "./PasswordField";

/**
 * 通常のログインフォーム UI
 */
export const LoginForm: React.FC<{
  email: string;
  password: string;
  rememberMe: boolean;
  loading: boolean;
  loadingMessage: string;
  error: string | null;
  recoverySuggestion: string | null;
  onEmailChange: (value: string) => void;
  onEmailBlur?: (value: string) => void; // PRODUCT_DEV-396 ログインID blur 時にパスキー利用可否判定 + 自動トリガー
  onPasswordChange: (value: string) => void;
  onRememberMeChange: (value: boolean) => void;
  onSubmit: (e: React.FormEvent) => void;
  onForgotPassword: () => void;
  // PRODUCT_DEV-421 alias ログインモード（URL `/:tenantId` 由来。設定されていればラベルを「エイリアス」に切替）
  aliasTenantId?: string | null;
}> = ({
  email,
  password,
  rememberMe,
  loading,
  loadingMessage,
  error,
  recoverySuggestion,
  onEmailChange,
  onEmailBlur,
  onPasswordChange,
  onRememberMeChange,
  onSubmit,
  onForgotPassword,
  aliasTenantId,
}) => {
  const isAliasMode = !!aliasTenantId;
  const [showPassword, setShowPassword] = useState(false);
  return (
    <AuthPageLayout>
      {/* ログインタイトル */}
      <h2 className="text-3xl font-bold text-center text-neutral-900 mb-8">
        ログイン
      </h2>

      {/* PRODUCT_DEV-421 alias モード時のテナントID表示（簡易版） */}
      {isAliasMode && (
        <p className="text-center text-sm text-neutral-500 mb-4 break-all">
          テナントID: {aliasTenantId}
        </p>
      )}

      {/* ログインフォーム */}
      <form onSubmit={onSubmit} className="space-y-6">
        {/* ログインID または エイリアス */}
        <div>
          <input
            id="login-email"
            type="text"
            placeholder={
              isAliasMode
                ? "エイリアスを入力してください"
                : "ログインIDを入力してください"
            }
            value={email}
            onChange={(e) => onEmailChange(e.target.value)}
            onBlur={(e) => {
              // PRODUCT_DEV-421 alias モード時はパスキー判定（login-options）を起動しない
              if (!isAliasMode) onEmailBlur?.(e.target.value);
            }}
            className="w-full px-6 py-4 border border-neutral-300 rounded-2xl text-neutral-900 placeholder-neutral-400 focus:outline-none focus:border-primary transition-colors bg-white"
            disabled={loading}
          />
        </div>

        {/* パスワード */}
        <PasswordField
          id="login-password"
          name="password"
          autoComplete="current-password"
          placeholder="パスワードを入力してください"
          value={password}
          onChange={onPasswordChange}
          show={showPassword}
          onToggle={() => setShowPassword((v) => !v)}
          disabled={loading}
          required={false}
        />

        {/* 利用規約とプライバシーポリシー */}
        <div className="text-center text-sm text-neutral-700">
          ログインをもって
          <a
            href="https://umeee.co.jp/terms_unizo"
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary hover:underline"
          >
            利用規約
          </a>
          および
          <br />
          <a
            href="https://umeee.co.jp/privacypolicy"
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary hover:underline"
          >
            プライバシーポリシー
          </a>
          に同意するものとします
        </div>

        {/* ログインボタン */}
        <LoadingButton loading={loading} loadingMessage={loadingMessage} label="ログイン" />

        {/* エラーメッセージ */}
        {error && <div className="mt-2 text-sm text-red-600">{error}</div>}
        {recoverySuggestion && (
          <div className="mt-1 text-sm text-neutral-700">
            {recoverySuggestion}
          </div>
        )}

        {/* 次回から自動的にログイン */}
        <div className="flex items-center">
          <input
            type="checkbox"
            id="rememberMe"
            checked={rememberMe}
            onChange={(e) => onRememberMeChange(e.target.checked)}
            className="w-6 h-6 border-2 border-neutral-300 rounded cursor-pointer"
          />
          <label
            htmlFor="rememberMe"
            className="ml-3 text-neutral-700 cursor-pointer"
          >
            次回から自動的にログイン
          </label>
        </div>

        {/* パスワードを忘れた方 */}
        <div className="text-center">
          <button
            type="button"
            onClick={onForgotPassword}
            className="text-sm text-primary hover:underline"
            disabled={loading}
          >
            パスワードを忘れた方
          </button>
        </div>
      </form>
    </AuthPageLayout>
  );
};
